import { useState, useEffect } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import { Album } from '../../../../types'
import { apiClient } from '../lib/api-client'
import { folderLogger } from '../lib/logger'

export const STORAGE_KEY = 'memoar_local_albums'
export const DELETED_ALBUMS_KEY = 'memoar_deleted_albums'

export interface AlbumCollection {
  albums: Album[]
  loading: boolean
  error: string | null
  hasUnsavedChanges: boolean
  setAlbums: Dispatch<SetStateAction<Album[]>>
  setHasUnsavedChanges: (value: boolean) => void
  fetchAlbums: (silent?: boolean) => Promise<void>
  updateAlbums: (newAlbums: Album[]) => void
}

// The local/backend/deleted-ids reconciliation: decides which albums a user
// actually sees. Takes no feedback dependency on purpose -- fetchAlbums only
// sets `error` state, every user-visible message is raised by the caller --
// so the merge is testable with no UI at all.
export function useAlbumCollection(currentUserId?: string): AlbumCollection {
  const [albums, setAlbums] = useState<Album[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  // Persists the given albums to localStorage. Called explicitly by
  // fetchAlbums and updateAlbums after each recomputes the album list --
  // nothing here watches `albums` itself, so neither call site is redundant.
  const saveToLocalStorage = (albumsToSave: Album[]) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(albumsToSave))
      folderLogger.debug('Albums saved to localStorage', { count: albumsToSave.length })
    } catch (error) {
      folderLogger.error('Failed to save albums to localStorage', { error })
    }
  }

  // Load from localStorage
  const loadFromLocalStorage = (): Album[] => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        folderLogger.debug('Albums loaded from localStorage', { count: parsed.length })
        return parsed
      }
    } catch (error) {
      folderLogger.error('Failed to load albums from localStorage', { error })
    }
    return []
  }

  // Track deleted albums to prevent them from reappearing on reload
  const getDeletedAlbumIds = (): Set<string> => {
    try {
      const stored = localStorage.getItem(DELETED_ALBUMS_KEY)
      if (stored) {
        return new Set(JSON.parse(stored))
      }
    } catch (error) {
      folderLogger.error('Failed to load deleted albums from localStorage', { error })
    }
    return new Set()
  }

  const fetchAlbums = async (silent = false) => {
    try {
      folderLogger.debug('Starting fetchAlbums request', { silent })
      if (!silent) {
        setLoading(true)
      }

      // Load local albums first
      const localAlbums = loadFromLocalStorage()

      // Get deleted album IDs to filter them out
      const deletedIds = getDeletedAlbumIds()

      // Fetch from backend
      const response = await apiClient.getAlbums()

      // Which Memoirs this user still has a stake in (SOL-185), from the latest
      // GET /api/memoirs. Only needed to prune; if it fails nothing is pruned.
      const missing = localAlbums.filter(
        (a) => !a.uuid.startsWith('temp_') && !response.albums.some((b) => b.uuid === a.uuid),
      )
      let liveMemoirIds: Set<string> | null = null
      if (missing.length > 0) {
        try {
          const list = await apiClient.getMemoirs()
          liveMemoirIds = new Set([
            ...list.memoirs.map((m) => m.uuid),
            ...list.invitations.filter((i) => i.acceptedAt).map((i) => i.uuid),
          ])
        } catch {
          liveMemoirIds = null
        }
      }
      // Provably gone: a real album of a Memoir the user neither owns nor was
      // handed, and did not claim. A backend that merely omits an album for a
      // moment (KV list lag) proves nothing, so that never prunes.
      const provablyGone = (a: Album): boolean =>
        !!liveMemoirIds && !!a.memoirId && !liveMemoirIds.has(a.memoirId)
        && a.role !== 'member' && !(currentUserId && a.claimedBy === currentUserId)

      // Smart merge: preserve local modifications
      const backendAlbumsMap = new Map(response.albums.map(a => [a.uuid, a]))
      const localAlbumsMap = new Map(localAlbums.map(a => [a.uuid, a]))

      const mergedAlbums: Album[] = []

      // Add all local albums, prioritizing local changes for real albums
      for (const localAlbum of localAlbums) {
        const backendAlbum = backendAlbumsMap.get(localAlbum.uuid)

        if (!backendAlbum) {
          if (!localAlbum.uuid.startsWith('temp_') && provablyGone(localAlbum)) {
            folderLogger.debug('Pruning album of a Memoir the user no longer has', { uuid: localAlbum.uuid })
            continue
          }
          // Local-only album (temp_*), keep as-is
          mergedAlbums.push(localAlbum)
          folderLogger.debug('Keeping local-only album', { uuid: localAlbum.uuid, name: localAlbum.name })
        } else {
          // Album exists in both - check if locally modified
          const hasLocalChanges = localAlbum.parentId !== backendAlbum.parentId ||
                                  localAlbum.name !== backendAlbum.name

          if (hasLocalChanges) {
            // Use local version but preserve backend metadata
            mergedAlbums.push({
              ...backendAlbum,
              parentId: localAlbum.parentId,
              name: localAlbum.name
            })
            folderLogger.debug('Preserving local changes for real album', {
              uuid: localAlbum.uuid,
              name: localAlbum.name,
              parentId: localAlbum.parentId
            })
          } else {
            // No local changes, use backend version
            mergedAlbums.push(backendAlbum)
          }
        }
      }

      // Add any backend albums not present locally (unless deleted)
      for (const backendAlbum of response.albums) {
        if (!localAlbumsMap.has(backendAlbum.uuid) && !deletedIds.has(backendAlbum.uuid)) {
          mergedAlbums.push(backendAlbum)
          folderLogger.debug('Adding new backend album', { uuid: backendAlbum.uuid, name: backendAlbum.name })
        } else if (deletedIds.has(backendAlbum.uuid)) {
          folderLogger.debug('Skipping deleted album from backend', { uuid: backendAlbum.uuid, name: backendAlbum.name })
        }
      }

      folderLogger.info('Albums fetched and merged successfully', {
        backendCount: response.albums.length,
        localCount: localAlbums.length,
        mergedCount: mergedAlbums.length,
        localOnlyCount: localAlbums.filter(a => !backendAlbumsMap.has(a.uuid)).length,
        modifiedCount: localAlbums.filter(a => {
          const backend = backendAlbumsMap.get(a.uuid)
          return backend && (a.parentId !== backend.parentId || a.name !== backend.name)
        }).length
      })

      setAlbums(mergedAlbums)
      saveToLocalStorage(mergedAlbums)

      // Has unsaved changes if any empty albums OR any modified real albums
      const hasEmptyAlbums = mergedAlbums.some(a => a.isEmpty)
      const hasModifiedRealAlbums = localAlbums.some(a => {
        const backend = backendAlbumsMap.get(a.uuid)
        return backend && (a.parentId !== backend.parentId || a.name !== backend.name)
      })
      setHasUnsavedChanges(hasEmptyAlbums || hasModifiedRealAlbums)
    } catch (err) {
      // If backend fails, use local albums
      const localAlbums = loadFromLocalStorage()
      if (localAlbums.length > 0) {
        folderLogger.warn('Backend failed, using local albums', { count: localAlbums.length })
        setAlbums(localAlbums)
        setHasUnsavedChanges(true)
      } else {
        folderLogger.error('Failed to fetch albums and no local backup', { error: err })
        setError(err instanceof Error ? err.message : 'Failed to load albums')
      }
    } finally {
      if (!silent) {
        setLoading(false)
      }
      folderLogger.debug('fetchAlbums completed')
    }
  }

  useEffect(() => {
    fetchAlbums(false)
  }, [])

  const updateAlbums = (newAlbums: Album[]) => {
    setAlbums(newAlbums)
    saveToLocalStorage(newAlbums)

    // Always mark as having unsaved changes when we make local modifications
    setHasUnsavedChanges(true)
  }

  return {
    albums,
    loading,
    error,
    hasUnsavedChanges,
    setAlbums,
    setHasUnsavedChanges,
    fetchAlbums,
    updateAlbums,
  }
}
